import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { BadgeCheck } from "lucide-react";
import { EASE } from "@/components/Reveal";
import useTelegramSubscribers from "@/hooks/useTelegramSubscribers";

export function AnimatedNumber({ value, trigger = true }) {
    const [display, setDisplay] = useState(0);
    const prevRef = useRef(0);
    useEffect(() => {
        if (!trigger) return;
        const start = prevRef.current;
        const delta = value - start;
        const duration = Math.min(1400, 500 + Math.abs(delta) / 40);
        let raf;
        const t0 = performance.now();
        const tick = (t) => {
            const p = Math.min((t - t0) / duration, 1);
            const eased = 1 - Math.pow(1 - p, 3);
            setDisplay(Math.round(start + delta * eased));
            if (p < 1) raf = requestAnimationFrame(tick);
            else prevRef.current = value;
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, [value, trigger]);
    return <span className="font-extrabold text-ink">{display.toLocaleString("it-IT")}</span>;
}

export default function SubscriberCounter() {
    const { status, count, pulse } = useTelegramSubscribers();

    if (status === "unavailable") {
        return (
            <p
                data-testid="subscriber-fallback"
                className="mt-8 text-xs font-medium text-ink/40"
            >
                Iscritti Telegram: dati temporaneamente non disponibili
            </p>
        );
    }

    return (
        <motion.div
            data-testid="subscriber-counter"
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0, scale: pulse ? [1, 1.045, 1] : 1 }}
            transition={{ duration: 0.7, ease: EASE }}
            className="glass mt-8 inline-flex items-center gap-3 rounded-2xl px-4 py-3 ring-1 ring-ink/5"
        >
            <img
                src="/logo.png"
                alt=""
                width={40}
                height={40}
                className="h-10 w-10 rounded-full ring-1 ring-ink/10"
            />
            <div className="text-left leading-tight">
                <p className="flex items-center gap-1.5 text-[13px] font-bold text-ink">
                    Sconti Incredibili
                    <BadgeCheck className="h-3.5 w-3.5 fill-telegram text-white" />
                </p>
                {status === "ok" ? (
                    <p className="mt-0.5 text-[12.5px] text-ink/60" data-testid="subscriber-count">
                        <AnimatedNumber value={count} /> persone ci seguono già
                    </p>
                ) : (
                    <span className="mt-1 block h-3 w-32 animate-pulse rounded-full bg-ink/10" />
                )}
            </div>
            <span className="ml-1 inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-emerald-600 ring-1 ring-emerald-200">
                <span className="pulse-dot h-1.5 w-1.5 rounded-full bg-emerald-500" />
                Live
            </span>
        </motion.div>
    );
}
